import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * OpeningCinematic
 * Fullscreen autoplay of the opening cinematic shown the first time a
 * newly-created character enters the game world. On end (or skip) the
 * player is taken to /game and the normal game-world load proceeds.
 *
 * Browsers may block autoplay-with-sound until the user interacts. We
 * still set autoPlay+playsInline (the click that submitted character
 * creation usually satisfies the gesture requirement), and fall back to
 * a "▶ Begin" overlay if play() rejects.
 */
const OpeningCinematic = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [needsTapToStart, setNeedsTapToStart] = useState(false);
  const [done, setDone] = useState(false);
  // v736.1: show a loading spinner until the video has buffered enough to
  // play. We start true and flip false on the first `playing` event.
  const [isBuffering, setIsBuffering] = useState(true);

  const finish = () => {
    if (done) return;
    setDone(true);
    navigate('/game', { replace: true });
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => setNeedsTapToStart(true));
    }
  }, []);

  // Allow Esc / Enter / Space to skip
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        finish();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startManually = () => {
    const v = videoRef.current;
    if (!v) return;
    setNeedsTapToStart(false);
    v.play().catch(() => { /* if it still fails, user can just skip */ });
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
    >
      <video
        ref={videoRef}
        src="/cinematics/loria-opening-cinematic.mp4"
        autoPlay
        playsInline
        preload="auto"
        onEnded={finish}
        onWaiting={() => setIsBuffering(true)}
        onStalled={() => setIsBuffering(true)}
        onPlaying={() => setIsBuffering(false)}
        onCanPlay={() => setIsBuffering(false)}
        onError={(e) => {
          // Log but do not auto-skip — a transient media error
          // (e.g. a brief decode hiccup) should not silently send the
          // player to /game without ever seeing the cinematic. The
          // Skip button is always available if playback truly fails.
          // eslint-disable-next-line no-console
          console.warn('[OpeningCinematic] video error', e?.nativeEvent || e);
          setNeedsTapToStart(true);
        }}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto'
        }}
      />

      {needsTapToStart && (
        <button
          type="button"
          onClick={startManually}
          style={{
            position: 'absolute',
            padding: '14px 32px',
            background: 'linear-gradient(180deg, #6a4a2a, #4a3018)',
            border: '2px solid #c9a84c',
            color: '#ffd96b',
            borderRadius: 32,
            cursor: 'pointer',
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: 1,
            boxShadow: '0 4px 14px rgba(0,0,0,0.6)'
          }}
        >
          ▶ Begin
        </button>
      )}

      {isBuffering && !needsTapToStart && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            pointerEvents: 'none',
            color: '#ffd96b',
          }}
          aria-live="polite"
        >
          <div
            style={{
              width: 56,
              height: 56,
              border: '4px solid rgba(201,168,76,0.25)',
              borderTopColor: '#c9a84c',
              borderRadius: '50%',
              animation: 'loria-cine-spin 0.9s linear infinite',
            }}
          />
          <div style={{ fontSize: 13, letterSpacing: 1.5, opacity: 0.85, textTransform: 'uppercase' }}>
            Loading cinematic…
          </div>
          <style>{`@keyframes loria-cine-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      <button
        type="button"
        onClick={finish}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          padding: '6px 14px',
          background: 'rgba(0,0,0,0.5)',
          border: '1px solid #c9a84c',
          color: '#ffd96b',
          borderRadius: 18,
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: 1
        }}
      >
        Skip ▶▶
      </button>
    </div>
  );
};

export default OpeningCinematic;
